import { CatalogType, DurationUnit, StabilityStage } from '../database/schema.js';
import { isDefined } from './functions.js';
import { Status } from './schemas.js';
const DURATION_UNIT_MS = {
    [DurationUnit.enum.MINUTES]: 60 * 1000,
    [DurationUnit.enum.HOURS]: 60 * 60 * 1000,
    [DurationUnit.enum.DAYS]: 24 * 60 * 60 * 1000,
};
const DURATION_UNIT_MINUTES = {
    [DurationUnit.enum.MINUTES]: 1,
    [DurationUnit.enum.HOURS]: 60,
    [DurationUnit.enum.DAYS]: 1440,
};
// Stage that governs a unit in use: reconstitution for dry substances, opening otherwise.
export function getPreparationStabilityStage(isDrySubstance) {
    return isDrySubstance ? StabilityStage.enum.AFTER_RECONSTITUTION : StabilityStage.enum.AFTER_OPENING;
}
/**
 * Required rules for an active-substance trade name: the open/reconstitution stage
 * and the preparation stage, from the rules assigned to the trade name itself (CY-412).
 * Solution and supportive-medication trade names are not checked.
 */
export function getMissingRequiredStabilityStages(input) {
    if (input.type !== CatalogType.enum.ACTIVE_SUBSTANCE)
        return [];
    const stages = new Set(input.ownStages);
    return [getPreparationStabilityStage(input.isDrySubstance), StabilityStage.enum.AFTER_PREPARATION].filter(stage => !stages.has(stage));
}
export function computeStabilityRuleExpiry(rule, referenceTime) {
    return referenceTime + rule.durationValue * DURATION_UNIT_MS[rule.durationUnit];
}
// Only the rules linked to the trade name count: a medicine form rule applies once it is assigned (CY-412).
export function collectTradeNameStabilityRules(tradeName) {
    return (tradeName.stabilityRules?.map(link => link.stabilityRule) ?? []).filter(isDefined);
}
/** Earliest expiry among the active rules of a given stage, or undefined when none apply. */
export function getEarliestStabilityRuleExpiry(rules, stage, referenceTime) {
    const expiries = rules
        .filter(rule => rule.status === Status.enum.active && rule.stabilityStage === stage)
        .map(rule => computeStabilityRuleExpiry(rule, referenceTime));
    if (!expiries.length)
        return undefined;
    return Math.min(...expiries);
}
// The rule that governs a prepared unit: after-preparation, else the reconstitution/opening rule.
export function selectPreparationStabilityRule(rules, options) {
    const forStage = (stage) => rules.filter(rule => rule.status === Status.enum.active && rule.stabilityStage === stage);
    const afterPreparation = forStage(StabilityStage.enum.AFTER_PREPARATION);
    const eligible = afterPreparation.length
        ? afterPreparation
        : forStage(getPreparationStabilityStage(options.isDrySubstance));
    const forCarrier = options.carrierDrugId ? eligible.filter(rule => rule.carrierDrugId === options.carrierDrugId) : [];
    const candidates = forCarrier.length ? forCarrier : eligible.filter(rule => !rule.carrierDrugId);
    if (!candidates.length)
        return null;
    return candidates.reduce((shortest, rule) => (windowMinutes(rule) < windowMinutes(shortest) ? rule : shortest));
}
function windowMinutes(rule) {
    return rule.durationValue * DURATION_UNIT_MINUTES[rule.durationUnit];
}
// Expiry of a prepared unit: earliest window across drug and carrier rules, capped by batch expiries.
export function getPreparationExpiry(input) {
    const candidates = [
        ...getApplicableStageExpiries(input.drugRules, getPreparationStabilityStage(input.drugIsDrySubstance), input.referenceTime),
        ...getApplicableStageExpiries(input.carrierRules, getPreparationStabilityStage(input.carrierIsDrySubstance), input.referenceTime),
        earliest(input.drugBatchExpiries),
        earliest(input.carrierBatchExpiries),
        earliest(input.returnExpiries),
    ].filter(isDefined);
    return candidates.length ? Math.min(...candidates) : undefined;
}
/**
 * The stability rule behind a prepared unit's use-by: among exactly the rules getPreparationExpiry weighs (active
 * After preparation or opening/reconstitution rules of the drug and the carrier), the one with the shortest window.
 * The label prints its storage text, so the storage always belongs to the rule that sets the use-by (CY-410).
 * A shorter batch expiry may still cap the use-by; the storage then remains that of the governing rule.
 * On a tie an After preparation rule wins, then a drug rule over a carrier rule.
 */
export function selectUseByStabilityRule(input) {
    const candidates = [
        ...useByCandidates(input.drugRules, getPreparationStabilityStage(input.drugIsDrySubstance), 0),
        ...useByCandidates(input.carrierRules, getPreparationStabilityStage(input.carrierIsDrySubstance), 1),
    ];
    if (!candidates.length)
        return null;
    return candidates.reduce((best, candidate) => (compareUseByCandidates(candidate, best) < 0 ? candidate : best)).rule;
}
function useByCandidates(rules, openedStage, source) {
    return rules
        .filter(rule => rule.status === Status.enum.active &&
        (rule.stabilityStage === StabilityStage.enum.AFTER_PREPARATION || rule.stabilityStage === openedStage))
        .map(rule => ({ rule, source }));
}
// shortest window first, then an After preparation rule, then a drug rule over a carrier rule
function compareUseByCandidates(a, b) {
    return windowMinutes(a.rule) - windowMinutes(b.rule) || stageRank(a.rule) - stageRank(b.rule) || a.source - b.source;
}
function stageRank(rule) {
    return rule.stabilityStage === StabilityStage.enum.AFTER_PREPARATION ? 0 : 1;
}
function getApplicableStageExpiries(rules, openedStage, referenceTime) {
    return [
        getEarliestStabilityRuleExpiry(rules, StabilityStage.enum.AFTER_PREPARATION, referenceTime),
        getEarliestStabilityRuleExpiry(rules, openedStage, referenceTime),
    ];
}
function earliest(values) {
    if (!values.length)
        return undefined;
    return Math.min(...values);
}
//# sourceMappingURL=stability-expiry.js.map